import { useState, useEffect } from "react";
import {
  Container,
  Stack,
  Grid,
  Card,
  Text,
  Title,
  Badge,
  Group,
  Button,
  Skeleton,
  ActionIcon,
  Paper,
} from "@mantine/core";
import {
  IconBook,
  IconCards,
  IconChartBar,
  IconPlus,
  IconRefresh,
  IconAlertCircle,
  IconMicrophone,
  IconMoodKid,
  IconUserPlus,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MobileChildSelector } from "./MobileChildSelector";
import { MobileParentActivityFeed } from "./MobileParentActivityFeed";
import { parentApi } from "@/features/parent/api/parentApi";
import { useAuthStore } from "@/features/auth/store/authStore";
import { ParentQuickReadButton } from "@/features/recommendations";
import { OnboardingChecklist } from "@/features/parent/components/OnboardingChecklist";
import { WeeklyRecapCard } from "@/features/parent/components/WeeklyRecapCard";

export function MobileParentDashboard() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch children
  const { data: children, isLoading: childrenLoading } = useQuery({
    queryKey: ["children", user?.id],
    queryFn: () => parentApi.getChildren(),
    enabled: !!user?.id,
  });

  // Set default selected child
  useEffect(() => {
    if (children && children.length > 0 && !selectedChildId) {
      setSelectedChildId(children[0].id);
    }
  }, [children, selectedChildId]);

  // Fetch stats for selected child
  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ["child-stats", selectedChildId],
    queryFn: () => parentApi.getChildStats(selectedChildId!),
    enabled: !!selectedChildId,
  });

  // Fetch recent assignments for selected child
  const { data: assignments } = useQuery({
    queryKey: ["child-assignments", selectedChildId],
    queryFn: () => parentApi.getChildAssignments(selectedChildId!),
    enabled: !!selectedChildId,
    select: (data) => data?.slice(0, 3), // Only show 3 most recent
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetchStats();
    setRefreshing(false);
  };

  const handleSelectThemeFromRecap = (themeId: string, childId: string) => {
    navigate(`/mobile/parent/assign/story?themeId=${themeId}&studentId=${childId}`);
  };

  const selectedChild = children?.find((c) => c.id === selectedChildId);
  const hasChildren = !childrenLoading && children && children.length > 0;

  if (!childrenLoading && (!children || children.length === 0)) {
    return (
      <Container size="sm" p="md">
        <Stack align="center" gap="md" py="xl">
          <IconMoodKid size={64} stroke={1.5} color="var(--mantine-color-grape-5)" />
          <Title order={2} ta="center">
            Welcome to Svara!
          </Title>
          <Text c="dimmed" ta="center" size="md">
            Add your first child to get started.
          </Text>
          <Button
            size="md"
            leftSection={<IconUserPlus size={18} />}
            onClick={() => navigate("/mobile/parent/children")}
          >
            Add Your First Child
          </Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="sm" p="md">
      <Stack gap="lg">
        {/* Header with child selector */}
        <Group justify="space-between" align="center">
          <Title order={2}>Dashboard</Title>
          <ActionIcon onClick={handleRefresh} loading={refreshing} variant="subtle" size="lg">
            <IconRefresh size={20} />
          </ActionIcon>
        </Group>

        {hasChildren && <OnboardingChecklist />}

        {/* Child Selector */}
        {childrenLoading ? (
          <Skeleton height={60} />
        ) : (
          <MobileChildSelector
            children={children || []}
            selectedChildId={selectedChildId}
            onChildSelect={setSelectedChildId}
          />
        )}

        {/* Weekly Recap for the selected child */}
        {selectedChildId && selectedChild && (
          <WeeklyRecapCard
            childId={selectedChildId}
            childDisplayName={selectedChild.name || ""}
            onSelectTheme={handleSelectThemeFromRecap}
          />
        )}


        {/* Quick Actions */}
        <div>
          <Text size="sm" fw={600} c="dimmed" mb="xs">
            Quick Actions
          </Text>
          {/* Prominent Quick Read button */}
          <ParentQuickReadButton
            studentId={selectedChildId}
            childName={selectedChild?.name}
            size="md"
            fullWidth
          />
          {selectedChild?.fluency_enabled && (
            <Button
              size="md"
              variant="light"
              color="grape"
              leftSection={<IconMicrophone size={18} />}
              fullWidth
              mt="xs"
              onClick={() => navigate(`/parent/fluency/start?studentId=${selectedChildId}`)}
            >
              Read Aloud with {selectedChild?.name}
            </Button>
          )}
          <Grid gutter="xs" mt="xs">
            <Grid.Col span={6}>
              <Button
                fullWidth
                leftSection={<IconBook size={18} />}
                variant="light"
                onClick={() => navigate("/mobile/parent/assign/story")}
              >
                Assign Story
              </Button>
            </Grid.Col>
            <Grid.Col span={6}>
              <Button
                fullWidth
                leftSection={<IconCards size={18} />}
                variant="light"
                color="violet"
                onClick={() => navigate("/mobile/parent/assign/flashcards")}
              >
                Assign Cards
              </Button>
            </Grid.Col>
            <Grid.Col span={6}>
              <Button
                fullWidth
                leftSection={<IconChartBar size={18} />}
                variant="light"
                color="green"
                onClick={() => navigate(`/mobile/parent/child/${selectedChildId}/progress`)}
              >
                View Progress
              </Button>
            </Grid.Col>
            <Grid.Col span={6}>
              <Button
                fullWidth
                leftSection={<IconPlus size={18} />}
                variant="light"
                color="gray"
                onClick={() => navigate("/mobile/parent/create-assignment")}
              >
                Create Custom
              </Button>
            </Grid.Col>
          </Grid>
        </div>

        {/* Pending Assignments */}
        {assignments && assignments.length > 0 && (
          <div>
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={600} c="dimmed">
                Pending Assignments
              </Text>
              <Text
                size="xs"
                c="blue"
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/mobile/parent/assignments")}
              >
                View All
              </Text>
            </Group>
            <Stack gap="xs">
              {assignments.map((assignment) => (
                <Paper key={assignment.id} p="sm" withBorder>
                  <Group justify="space-between">
                    <div>
                      <Text size="sm" fw={500}>
                        {assignment.title}
                      </Text>
                      <Text size="xs" c="dimmed">
                        Due {new Date(assignment.dueDate).toLocaleDateString()}
                      </Text>
                    </div>
                    <Badge
                      variant="dot"
                      color={
                        assignment.status === "completed"
                          ? "green"
                          : assignment.status === "overdue"
                            ? "red"
                            : "blue"
                      }
                    >
                      {assignment.status}
                    </Badge>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </div>
        )}

        {/* Activity Feed */}
        <div>
          <Text size="sm" fw={600} c="dimmed" mb="xs">
            Recent Activity
          </Text>
          <MobileParentActivityFeed childId={selectedChild?.user_id} limit={5} />
        </div>

        {/* Alert if child needs attention */}
        {stats?.needsAttention && (
          <Card p="sm" withBorder style={{ borderColor: "var(--mantine-color-yellow-5)" }}>
            <Group gap="xs">
              <IconAlertCircle size={20} color="var(--mantine-color-yellow-6)" />
              <div style={{ flex: 1 }}>
                <Text size="sm" fw={500}>
                  {selectedChild?.name} needs attention
                </Text>
                <Text size="xs" c="dimmed">
                  {stats.attentionReason}
                </Text>
              </div>
            </Group>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
